import { type JSX } from 'react';

import { Anmeldehuelle, type Zustand } from './anmeldung/Anmeldehuelle';
import { Trainerkalender } from './trainerbereich/Trainerkalender';
import { CRMADRESSE } from './adressen';

/**
 * Der Seitentitel je Maske — und keiner nennt das CRM.
 *
 * Wer hier landet, hat mit dem CRM nichts zu tun (RES-28: „Zugang ohne
 * vollwertiges CRM-Konto"). Ein Titel, der es nennt, wäre der erste Hinweis auf
 * einen Bereich, den es für ihn nicht gibt — und im Browserverlauf, in der
 * Lesezeichenliste und in der Ansage des Vorleseprogramms steht genau dieser
 * Titel.
 */
const TITEL: Record<Zustand['art'], string> = {
  pruefe: 'Anmeldung wird geprüft — Trainerbereich',
  anmeldung: 'Anmeldung — Trainerbereich',
  faktor: 'Zweiten Faktor einrichten — Trainerbereich',
  wechsel: 'Passwort vergeben — Trainerbereich',
  angemeldet: 'Meine Einsätze',
};

/**
 * DER TRAINERBEREICH (`trainer.html`, RES-27 bis RES-32).
 *
 * ============================================================================
 * EIN EIGENER EINSTIEGSPUNKT, NICHT NUR EINE EIGENE ADRESSE
 * ============================================================================
 *
 * Bis zum 11.09.2026 lag der Kalender im CRM-Bündel: Die Weiche stand in
 * App.tsx, und ein Trainer lud auf seinem Telefon 1,37 MB JavaScript —
 * Kundenakte, Faktura, Tagesplan —, um eine Maske zu benutzen. Daten standen
 * darin keine; die holt erst ein Aufruf mit seinem Schlüssel, und der Server
 * gibt ihm nur Eigenes (RES-32). Aber es war Code, den er nicht braucht, auf
 * dem Gerät, das am weitesten draußen steht.
 *
 * Seitdem ist dies ein eigener Einstiegspunkt. Was er einbindet, steht oben —
 * drei Dateien, und die Arbeitsfläche ist keine davon. Rollup teilt das Bündel
 * entlang dieser Einfuhren; der Trainer bekommt die Anmeldung und seinen
 * Kalender.
 *
 * DAS IST KEINE BEQUEMLICHKEIT, SONDERN DIE GRENZE AUS RES-30: „Sichtbar sind
 * ausschließlich die Angaben nach RES-31; Kundendaten, Preise und eine
 * CRM-Navigation erscheinen nicht." Eine Navigation, die im Bündel liegt und
 * nur nicht angezeigt wird, ist eine Navigation, die beim nächsten Umbau
 * wieder auftaucht. Diese liegt nicht darin.
 */
export function TrainerApp(): JSX.Element {
  return (
    <Anmeldehuelle
      titel={TITEL}
      angemeldet={(benutzer, abmelden) => {
        /*
         * ====================================================================
         * HIER HERRSCHT DIE KONTOART UND NICHT DAS RECHT (RES-28)
         * ====================================================================
         *
         * Ein Operations Manager hat `tagesplan/sehen` ebenfalls und gehört
         * trotzdem nicht hierher — er hat ein Mitarbeiterkonto und die
         * Arbeitsfläche. Was den Trainerbereich bestimmt, ist das KONTO:
         * Kontoart `dienstleister` mit hinterlegter Ressource (Migration 0178).
         *
         * DIE PRÜFUNG HIER IST DIE BEQUEMLICHKEIT, NICHT DIE SICHERHEIT. Der
         * Server gibt unter `/api/v1/dienstleister` nur Eigenes heraus und
         * verlangt ein Konto mit Ressource; wer sich hier durchklickte, sähe
         * eine leere Maske und drei Fehlermeldungen. Diese Weiche sagt ihm
         * stattdessen, wo er hingehört (ERR-01).
         */
        if (benutzer.kontoart !== 'trainer') {
          return (
            <main className="dlk">
              <header className="dlk__kopf">
                <h1 className="dlk__titel" tabIndex={-1}>
                  Hier geht es nicht weiter
                </h1>
              </header>
              <p>
                Guten Tag, {benutzer.name}. Dieser Bereich gehört den Trainern und
                Dienstleistern. Ihr Konto ist ein Mitarbeiterkonto — es arbeitet in der
                Oberfläche des Hauses.
              </p>
              <p>
                <a className="knopf knopf--haupt" href={CRMADRESSE}>
                  Zur Arbeitsoberfläche
                </a>
              </p>
              <p className="hinweiszeile">
                Ihre Anmeldung gilt dort weiter. Sollten Sie als Trainer eingeplant werden
                und hier hereinkommen wollen, richtet die Disposition den Zugang an Ihrer
                Ressource ein.
              </p>
              <p>
                <button type="button" className="knopf knopf--klein" onClick={abmelden}>
                  Abmelden
                </button>
              </p>
            </main>
          );
        }

        return <Trainerkalender beiAbmelden={abmelden} />;
      }}
    />
  );
}
